import React, { useCallback } from 'react'

import { Icon, ChevronRight } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, array, shape, bool } from 'prop-types'

import ModelIcon from 'Pods/Shared/ModelIcon/ModelIcon'

import { getListingPagePaths } from 'Utils/getListingPagePaths'
import { sanitizePath } from 'Utils/sanitizePath'
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

import SubcategoryMenu from './Components/SubcategoryMenu/SubcategoryMenu'
import { useSubcategoryMenu } from './Hooks/useSubcategoryMenu'

const localePrefixes = ['de', 'es', 'fr', 'pt', 'ru', 'ja', 'zh-cn']

function AssetItem({ title, icon, href, subcategories = EMPTY_ARRAY, actionsConfig = EMPTY_OBJECT }) {
  const {
    isSubmenuOpen,
    hoverZoneRef,
    handleArrowClick,
    handleCloseSubmenu,
    handleHoverZoneMouseEnter,
    handleHoverZoneMouseLeave,
    handleSubmenuFocus,
    handleSubmenuBlur,
  } = useSubcategoryMenu()

  const breakpoint = useBreakpoint()
  const isMobile = ['xs', 'sm'].includes(breakpoint)

  const handleLinkClick = useCallback((event, destinationPath) => {
    if (!destinationPath) {
      return
    }

    // Sanitize the destination path (decode URL encoding and convert Unicode to ASCII)
    const sanitizedDestinationPath = sanitizePath(destinationPath)

    const currentPath = window.location.pathname
    const pathSegments = currentPath.split('/').filter(Boolean)
    const firstSegment = pathSegments[0]?.toLowerCase()

    const hasLocalePrefix = firstSegment && localePrefixes.includes(firstSegment)

    let isOn3dModelsCategoryPage = false
    let isOn3dPrintModelsCategoryPage = false
    const itemPageUmbrellaElement = document.querySelector('#listing-page-elements')
    const defaultPaths = getListingPagePaths(null)
    const localizedPaths = hasLocalePrefix ? getListingPagePaths(firstSegment) : null

    if (firstSegment === defaultPaths.modelsPath && pathSegments[1]) {
      isOn3dModelsCategoryPage = true
    } else if (hasLocalePrefix) {
      if (localizedPaths && pathSegments[1] === localizedPaths.modelsPath && pathSegments[2]) {
        isOn3dModelsCategoryPage = true
      } else if (pathSegments[1] === defaultPaths.modelsPath && pathSegments[2]) {
        isOn3dModelsCategoryPage = true
      }
    }

    if (firstSegment === defaultPaths.printModelsPath && pathSegments[1]) {
      isOn3dPrintModelsCategoryPage = true
    } else if (hasLocalePrefix) {
      if (localizedPaths && pathSegments[1] === localizedPaths.printModelsPath && pathSegments[2]) {
        isOn3dPrintModelsCategoryPage = true
      } else if (pathSegments[1] === defaultPaths.printModelsPath && pathSegments[2]) {
        isOn3dPrintModelsCategoryPage = true
      }
    }

    if (!itemPageUmbrellaElement || (!isOn3dModelsCategoryPage && !isOn3dPrintModelsCategoryPage)) {
      return
    }

    const normalizedDestinationPath = sanitizedDestinationPath.startsWith('/')
      ? sanitizedDestinationPath
      : `/${sanitizedDestinationPath}`
    const destinationPathSegments = normalizedDestinationPath.split('/').filter(Boolean)
    const destinationPathFirstSegment = destinationPathSegments[0]?.toLowerCase()

    const destinationHasLocalePrefix =
      destinationPathFirstSegment && localePrefixes.includes(destinationPathFirstSegment)
    const destinationLocalizedPaths = destinationHasLocalePrefix
      ? getListingPagePaths(destinationPathFirstSegment)
      : null

    const is3dModelsCategoryLink =
      destinationPathFirstSegment === defaultPaths.modelsPath ||
      (destinationHasLocalePrefix &&
        ((destinationLocalizedPaths && destinationPathSegments[1] === destinationLocalizedPaths.modelsPath) ||
          destinationPathSegments[1] === defaultPaths.modelsPath))
    const is3dPrintModelsCategoryLink =
      destinationPathFirstSegment === defaultPaths.printModelsPath ||
      (destinationHasLocalePrefix &&
        ((destinationLocalizedPaths && destinationPathSegments[1] === destinationLocalizedPaths.printModelsPath) ||
          destinationPathSegments[1] === defaultPaths.printModelsPath))

    if (is3dModelsCategoryLink || is3dPrintModelsCategoryLink) {
      event.preventDefault()

      const navElementId = is3dModelsCategoryLink ? '#navigation-3d-models' : '#navigation-3d-print'
      const navElement = document.querySelector(navElementId)
      if (navElement && !isMobile) {
        navElement.classList.add('nav-dropdown-closed')
        setTimeout(() => {
          navElement.classList.remove('nav-dropdown-closed')
        }, 100)
      }

      window.dispatchEvent(
        new CustomEvent('listing-page:navigate-category', {
          detail: { href: sanitizedDestinationPath },
        })
      )

      if (isMobile) {
        window.dispatchEvent(
          new CustomEvent('listing-page:navigate-category-mobile')
        )
      }
    }
  }, [isMobile])

  // TODO: Remove when subscription menu is live
  const subscriptionMenuEnabled = actionsConfig?.subscriptionMenuEnabled
  const subscriptionFlowEnabled = actionsConfig?.subscriptionFlowEnabled
  const shouldHide = icon === 'subscription' && (!subscriptionMenuEnabled || !subscriptionFlowEnabled)

  if (shouldHide) {
    return null
  }

  return (
    <div
      className="asset-item"
      onMouseEnter={handleHoverZoneMouseEnter}
      onMouseLeave={handleHoverZoneMouseLeave}
      onFocus={handleSubmenuFocus}
      onBlur={handleSubmenuBlur}
      ref={hoverZoneRef}
    >
      <a className="asset-item__link" href={href} onClick={(event) => handleLinkClick(event, href)}>
        <ModelIcon icon={icon} width="24" height="24" />
        <span className="asset-item__title">{title}</span>

        {!isMobile && subcategories && subcategories.length > 0 && (
          <Icon color="dark" size="small" svgComponent={ChevronRight} className="asset-item__arrow" />
        )}
      </a>

      {isMobile && subcategories && subcategories.length > 0 && (
        <Icon
          color="dark"
          size="small"
          svgComponent={ChevronRight}
          className="asset-item__arrow"
          style={{ cursor: 'pointer' }}
          onClick={handleArrowClick}
        />
      )}

      {subcategories && subcategories.length > 0 && (
        <SubcategoryMenu
          subcategories={subcategories}
          categoryTitle={title}
          isOpen={isSubmenuOpen}
          onClose={handleCloseSubmenu}
          handleLinkClick={handleLinkClick}
        />
      )}
    </div>
  )
}

AssetItem.propTypes = {
  title: string.isRequired,
  icon: string.isRequired,
  href: string,
  subcategories: array,
  actionsConfig: shape({
    subscriptionMenuEnabled: bool,
    subscriptionFlowEnabled: bool,
    isActiveSubscriber: bool,
  }),
}

export default AssetItem
